import { useSeoApi } from '@/api/sdk-api';
import { WS_AGENT } from '@/constants/Constants';
import { StorageAgent } from '@/utils/agents/storage';
import {
  type BmNoticeOut,
  BmNoticeTargets,
  BmNoticeTypes,
  BmNoticeVariants,
  WsEventOuts,
} from '@marpla/api-sdk';
import { TopBannerBase } from '@marpla/ui/components/TopBanner';
import Typography from '@marpla/ui/components/Typography';
import { type FC, useCallback, useEffect, useState } from 'react';

import { enqueueSnackbar } from '../SnackBar';

type ToastVariant = 'default' | 'error' | 'success' | 'warning' | 'info';

const DELETED_NOTICE_EVENT = 'deleted_notice';
const storageAgent = new StorageAgent();

const getHtmlText = (text: string): string => {
  const reg = /\S+<([^>\s]+)>/g; // находит строки формата text<src>

  const targetText = text.match(reg);

  let newText = text;

  targetText?.forEach((t) => {
    const links = t.match(/<([^\s]+)>/g);

    const linkWithBrackets = links?.[0];

    if (linkWithBrackets) {
      const newT = t.replace(linkWithBrackets, '');

      const src = linkWithBrackets.slice(1, -1);

      newText = newText.replace(t, `<a href="${src}" target="_blank">${newT}</a>`);
    }
  });

  return newText;
};

const getNotificationStorageKey = (id: string): string => `notification_${id}`;

const getToastVariant = (variant: BmNoticeVariants): ToastVariant => {
  switch (variant) {
    case BmNoticeVariants.ERROR:
      return 'error';
    case BmNoticeVariants.SUCCESS:
      return 'success';
    case BmNoticeVariants.WARNING:
    case BmNoticeVariants.YELLOW:
      return 'warning';
    case BmNoticeVariants.INFO:
    case BmNoticeVariants.VIOLET:
    case BmNoticeVariants.BLACK:
    default:
      return 'info';
  }
};

const isClosableBannerVariant = (variant: BmNoticeVariants): boolean =>
  variant !== BmNoticeVariants.ERROR;
const isBannerNotice = (type: BmNoticeTypes): boolean =>
  type === BmNoticeTypes.ALERT || type === BmNoticeTypes.MARQUEE;

interface NotificationBackendEventProps {
  event: BmNoticeOut;
}

const NotificationBackendEvent: FC<NotificationBackendEventProps> = ({ event }) => {
  const [message, setMessage] = useState<string | null>(null);
  const [variant, setVariant] = useState<BmNoticeVariants>(BmNoticeVariants.WARNING);

  useEffect(() => {
    const savedNotification = storageAgent.has(getNotificationStorageKey(event._id));

    if (savedNotification) {
      setMessage(null);

      return undefined;
    }

    const startDate = new Date(event.startedAt);
    const endDate = new Date(event.expiredAt);
    const now = new Date();
    const timeUntilStart = startDate.getTime() - now.getTime();
    const timeUntilEnd = endDate.getTime() - now.getTime();

    if (!Number.isFinite(timeUntilStart) || !Number.isFinite(timeUntilEnd) || timeUntilEnd <= 0) {
      setMessage(null);

      return undefined;
    }

    const startEvent = (): void => {
      if (isBannerNotice(event.type)) {
        setMessage(event.content);
        setVariant(event.variant);

        return;
      }

      if (event.type === BmNoticeTypes.TOAST) {
        enqueueSnackbar(event.content, {
          variant: getToastVariant(event.variant),
          autoHideDuration: Math.max(timeUntilEnd - Math.max(timeUntilStart, 0), 0),
        });
      }
    };

    const endEvent = (): void => {
      setMessage(null);
    };

    const startTimeout = timeUntilStart > 0 ? setTimeout(startEvent, timeUntilStart) : undefined;
    const endTimeout = setTimeout(endEvent, timeUntilEnd);

    if (timeUntilStart <= 0) {
      startEvent();
    }

    return () => {
      if (startTimeout) {
        clearTimeout(startTimeout);
      }

      clearTimeout(endTimeout);
    };
  }, [event]);

  if (!message) {
    return null;
  }

  const handleClose = (): void => {
    storageAgent.set(getNotificationStorageKey(event._id), event);
    setMessage(null);
  };

  const content = (
    <Typography
      dangerouslySetInnerHTML={{ __html: getHtmlText(message) }}
      style={{ color: '#4d4d4d' }}
      shade={0}
    >
      {null}
    </Typography>
  );

  return (
    <TopBannerBase
      isMarquee={event.type === BmNoticeTypes.MARQUEE}
      message={content}
      variant={variant}
      onClose={isClosableBannerVariant(variant) ? handleClose : undefined}
    />
  );
};

export default function NotificationBackend(): JSX.Element {
  const { getNotifications } = useSeoApi();
  const [notifications, setNotifications] = useState<BmNoticeOut[]>([]);

  const handleEventDisplay = useCallback((result: BmNoticeOut): void => {
    if (
      !result?.type ||
      !result?.content ||
      !result?.startedAt ||
      !result?.expiredAt ||
      result.type === BmNoticeTypes.TOAST ||
      result.target !== BmNoticeTargets.SEO
    ) {
      return;
    }

    setNotifications((currentNotifications) => {
      const hasNotice = currentNotifications.some((notice) => notice._id === result._id);

      if (hasNotice) {
        return currentNotifications.map((notice) => (notice._id === result._id ? result : notice));
      }

      return [...currentNotifications, result];
    });
  }, []);

  const handleDeleteNotice = useCallback((noticeId: string): void => {
    setNotifications((currentNotifications) =>
      currentNotifications.filter((notice) => notice._id !== noticeId),
    );
  }, []);

  useEffect(() => {
    (getNotifications as () => Promise<BmNoticeOut[]>)()
      .then((response: BmNoticeOut[]) => {
        if (!Array.isArray(response)) {
          return;
        }

        const seoNotices = response.filter((n) => n.target === BmNoticeTargets.SEO);

        setNotifications(seoNotices);
      })
      .catch(() => {});
  }, [getNotifications]);

  useEffect(() => {
    WS_AGENT.events.on(WsEventOuts.NEW_NOTICE, handleEventDisplay);

    return () => {
      WS_AGENT.events.off(WsEventOuts.NEW_NOTICE, handleEventDisplay);
    };
  }, [handleEventDisplay]);

  useEffect(() => {
    WS_AGENT.events.on(DELETED_NOTICE_EVENT, handleDeleteNotice);

    return () => {
      WS_AGENT.events.off(DELETED_NOTICE_EVENT, handleDeleteNotice);
    };
  }, [handleDeleteNotice]);

  return (
    <>
      {notifications.map((event) => (
        <NotificationBackendEvent key={event._id} event={event} />
      ))}
    </>
  );
}
